import { useApolloClient } from "@apollo/client";
import { Add, Visibility, VisibilityOff } from "@mui/icons-material";
import IconLayers from "@mui/icons-material/Layers";
import { Box, Button, Stack } from "@mui/material";
import {
    RichTreeView,
    TreeItemCheckbox,
    TreeItemContent,
    TreeItemLabel,
    TreeItemProps,
    TreeItemProvider,
    TreeItemRoot,
    useTreeItem,
    useTreeItemModel,
} from "@mui/x-tree-view";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { GqlPermissions } from "~/__generated__";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";
import { MapPillPanel } from "~/modules/map/components/MapPillPanel";
import { useLayerVisibility } from "~/modules/project/hooks/useLayerVisibility";
import { ProjectMapEditorScreen_GetProjectDocument } from "~/modules/project/screens/ProjectMapEditorScreen/queries.generated";

import { MapEditorGetAllLayerFeaturesDocument } from "../../api/useMapEditorGetAllLayerFeatures.generated";
import MapEditorLayerCreateDialog from "../MapEditorLayerCreateDialog";
import { MapPillPanelLayersFragment } from "./fragments.generated";

type MapPillPanelProps = {
    layers?: MapPillPanelLayersFragment[];
    projectId: string;
    canCreateLayer?: boolean;
} & (
    | {
          selectable?: true;
          onSelect: (layerId: string) => void;
          selectedLayerId?: string;
      }
    | {
          selectable: false;
          onSelect?: undefined;
          selectedLayerId?: undefined;
      }
);

export function MapPillPanelLayers({
    onSelect,
    selectedLayerId,
    layers,
    projectId,
    selectable = true,
    canCreateLayer = true,
}: MapPillPanelProps) {
    const { visibleLayerIds, showAllLayersByDefault, setVisibleLayerIds } = useLayerVisibility();
    const client = useApolloClient();

    const { t } = useTranslation("mapEditor");
    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);

    const [showCreateDialog, setShowCreateDialog] = useState(false);

    const allLayerIds = useMemo(() => layers?.map(l => l.id) ?? [], [layers]);

    const allVisible = showAllLayersByDefault ? true : visibleLayerIds.length === allLayerIds.length && allLayerIds.length > 0;

    const handleNewLayerAdded = async (layerId: string) => {
        onSelect?.(layerId);
        setShowCreateDialog(false);

        await client.refetchQueries({
            include: [MapEditorGetAllLayerFeaturesDocument, ProjectMapEditorScreen_GetProjectDocument],
        });
    };

    return (
        <MapPillPanel icon={<IconLayers />} title={t("mapEditorLayersTree.title")} hasDot={allVisible === false}>
            <Stack paddingBlockEnd={1}>
                {layers && (
                    <>
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button
                                size="small"
                                onClick={() => setVisibleLayerIds(allVisible ? [] : allLayerIds)}
                                endIcon={allVisible ? <VisibilityOff /> : <Visibility />}
                                sx={{ paddingInline: 2.4, paddingBlock: 1 }}
                            >
                                {t(allVisible ? "mapEditorLayersTree.hideAll" : "mapEditorLayersTree.showAll")}
                            </Button>
                        </Box>
                        <RichTreeView
                            checkboxSelection
                            multiSelect
                            items={layers ?? []}
                            getItemId={layer => layer.id}
                            getItemLabel={layer => layer.name}
                            selectedItems={showAllLayersByDefault ? allLayerIds : visibleLayerIds}
                            onItemClick={(_, selectedItemId) => {
                                if (selectable === false) return;
                                onSelect?.(selectedItemId);
                            }}
                            onSelectedItemsChange={(_, itemIds) => {
                                setVisibleLayerIds(itemIds);
                            }}
                            slots={{
                                item: (props: TreeItemProps) => (
                                    <LayerTreeItem {...props} selectedLayerId={selectedLayerId} selectable={selectable} />
                                ),
                            }}
                            slotProps={{ item: { selectedLayerId, selectable } as LayerTreeItem }}
                        />
                    </>
                )}

                {isAdmin && canCreateLayer && (
                    <Button startIcon={<Add />} onClick={() => setShowCreateDialog(true)} sx={{ margin: 2 }}>
                        {t("mapEditorLayersTree.btnNewLayer")}
                    </Button>
                )}

                <MapEditorLayerCreateDialog
                    projectId={projectId}
                    open={showCreateDialog}
                    onClose={() => setShowCreateDialog(false)}
                    onFinished={handleNewLayerAdded}
                />
            </Stack>
        </MapPillPanel>
    );
}

type LayerTreeItem = TreeItemProps & { selectedLayerId?: string; selectable: boolean };

const LayerTreeItem = React.forwardRef(
    ({ id, itemId, label, children, selectedLayerId, selectable }: LayerTreeItem, ref: React.Ref<HTMLLIElement>) => {
        const { getRootProps, getContentProps, getCheckboxProps, getLabelProps, getContextProviderProps } = useTreeItem({
            id,
            itemId,
            children,
            label,
            rootRef: ref,
        });

        const layer = useTreeItemModel<MapPillPanelLayersFragment>(itemId);
        if (!layer) return;

        return (
            <TreeItemProvider {...getContextProviderProps()}>
                <TreeItemRoot {...getRootProps()}>
                    <TreeItemContent
                        sx={{
                            display: "flex",
                            columnGap: 2,
                            paddingInline: 2,
                            ...(selectable === false && {
                                "&:hover, &[data-focused]": {
                                    backgroundColor: "transparent",
                                    cursor: "default",
                                },
                            }),
                        }}
                        {...getContentProps()}
                        data-selected={(selectable && selectedLayerId === itemId) || undefined}
                    >
                        <TreeItemColorIcon color={layer.color} />
                        <TreeItemLabel {...getLabelProps()} />
                        <TreeItemCheckbox
                            size="small"
                            color="default"
                            icon={<VisibilityOff />}
                            checkedIcon={<Visibility />}
                            {...getCheckboxProps()}
                        />
                    </TreeItemContent>
                </TreeItemRoot>
            </TreeItemProvider>
        );
    },
);

function TreeItemColorIcon({ color }: { color: string }) {
    const size = 16;
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            fill="none"
            aria-hidden="true"
        >
            <circle cx={size / 2} cy={size / 2} r={size / 2} fill={color} />
        </svg>
    );
}
